import React from 'react';
import { SectionFigure } from './SectionFigure';
import { sectionImages } from '../data/sectionImages';
const headingClass = 'pt-4 font-serif text-[1.5rem] font-bold leading-snug tracking-tight text-ink sm:text-[1.7rem]';
function ListBlock({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2 pl-1" data-id="element-24" data-mp="21br69zbijn6y">
      {items.map(item => <li key={item} className="flex gap-3" data-id="element-25" data-mp="21dkal7ynlvbs">
          <span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-26" data-mp="1dl4xu6xlk0wf" />
          <span data-id="element-27" data-mp="19lcwofaj8x13">{item}</span>
        </li>)}
    </ul>;
}
export function ArticleBody() {
  return <div className="mt-8 space-y-5 font-serif text-[1.08rem] leading-[1.75] text-ink sm:text-[1.14rem]" data-id="element-28" data-mp="1nl47y89nzzbe">
      <p data-id="element-29" data-mp="1ik84twx50h71">
        <span className="float-left mr-2 mt-1 font-serif text-[3.4rem] font-bold leading-[0.8] text-brand" data-id="element-30" data-mp="1ijeuyn2vtbko">
          A
        </span>
        sus 38 años, María pensaba que su cansancio de media tarde era simplemente «parte de la
        vida adulta».
      </p>
      <p data-id="element-31" data-mp="ysjupvevwmd8">
        Trabajaba, atendía su casa, intentaba comer mejor y hacía lo posible por mantenerse
        activa. Pero había algo que se repetía casi todos los días: después del almuerzo venía
        una sensación de pesadez, sueño, falta de concentración y, unas horas más tarde, una
        necesidad muy fuerte de comer algo dulce.
      </p>
      <p className="border-l-4 border-brand bg-paper py-3 pl-4 font-semibold" data-id="element-32" data-mp="1dmyh85xnqdwn">
        No era hambre real. Era como si su cuerpo le pidiera azúcar con urgencia.
      </p>
      <p data-id="element-33" data-mp="l15d9t0mkt8a">
        Lo más frustrante era que María sentía que ya estaba haciendo «lo correcto». Comía
        ensaladas, evitaba los excesos entre semana, intentaba no comprar postres y hasta había
        probado varias dietas populares. Aun así, sentía que su energía seguía inestable y que su
        abdomen se inflamaba con facilidad.
      </p>
      <p data-id="element-34" data-mp="29vzv8nqhkbf">Durante mucho tiempo pensó que el problema era ella.</p>
      <p data-id="element-35" data-mp="281exucgps7c">
        Que le faltaba disciplina. Que tenía poca fuerza de voluntad. Que después de los 30 su
        metabolismo simplemente «se había dañado».
      </p>
      <p data-id="element-36" data-mp="3lg98tw2td8se">
        Pero lo que muchas mujeres como María no saben es que, en algunos casos, el problema no
        está solamente en la cantidad de comida, sino en cómo esa comida impacta la glucosa en
        sangre.
      </p>

      <h2 className={headingClass} data-id="element-37" data-mp="3v5ssv529j8ou">La trampa de la «montaña rusa» de glucosa</h2>
      <SectionFigure {...sectionImages.rollercoaster} data-id="element-38" data-mp="37grj9ehp1rhz" />
      <p data-id="element-39" data-mp="32gmznflhognj">
        Cuando una comida contiene carbohidratos de absorción rápida, harinas refinadas, azúcar o
        incluso combinaciones aparentemente «normales», la glucosa en sangre puede elevarse con
        rapidez.
      </p>
      <p data-id="element-40" data-mp="3cejgmo13y5tk">
        Eso no significa que todos los carbohidratos sean malos. Tampoco significa que haya que
        eliminarlos por completo.
      </p>
      <p data-id="element-41" data-mp="wprj6ldlba1b">
        El problema aparece cuando el cuerpo vive demasiadas subidas y bajadas durante el día.
        Muchas mujeres describen este patrón como una especie de montaña rusa:
      </p>
      <ListBlock items={['Primero comen.', 'Luego sienten sueño o pesadez.', 'Después baja la energía.', 'Más tarde aparece el antojo.', 'Y al final llega la culpa.']} data-id="element-42" data-mp="za62qsppojwr" />
      <p data-id="element-43" data-mp="u8gshfabca9q">
        Este ciclo puede repetirse durante semanas, meses o incluso años, sin que la persona
        entienda por qué se siente tan agotada aunque esté «comiendo saludable».
      </p>
      <p data-id="element-44" data-mp="1my74nisdx237">
        De hecho, algunas comidas que parecen ligeras pueden provocar poca saciedad si no están
        bien estructuradas. Por ejemplo, empezar el día solo con café y pan, comer fruta sola
        cuando hay mucha hambre, o almorzar primero arroz, pasta o pan antes de incluir proteína,
        vegetales o grasas saludables.
      </p>
      <p data-id="element-45" data-mp="20w5i8lyvsuhn">
        Por eso, en los últimos años, ha cobrado fuerza un concepto sencillo: el orden en que
        comes puede importar más de lo que crees.
      </p>

      <h2 className={headingClass} data-id="element-46" data-mp="i03qrhk3p1w2">El error común: pensar solo en calorías</h2>
      <SectionFigure {...sectionImages.calories} data-id="element-47" data-mp="d10qeaf2fjuf" />
      <p data-id="element-48" data-mp="czfscatjlirt">
        Durante décadas, muchas mujeres aprendieron que para mejorar su cuerpo solo tenían que
        «comer menos y moverse más».
      </p>
      <p data-id="element-49" data-mp="40klqxml66nz">
        Y aunque el balance energético importa, esa explicación se queda corta para muchas
        personas.
      </p>
      <p data-id="element-50" data-mp="10p0dn0d64s49">Porque dos comidas con calorías similares pueden sentirse muy diferentes en el cuerpo.</p>
      <p data-id="element-51" data-mp="vsjpi65yydr6">
        Una puede dejarte satisfecha, enfocada y con energía estable. Otra puede dejarte con
        sueño, ansiedad por dulce y hambre una hora después.
      </p>
      <p data-id="element-52" data-mp="mzgcfyz8k2x2">
        La diferencia no siempre está en la cantidad. A veces está en la estructura de la comida,
        la presencia de fibra, la proteína, las grasas saludables y el orden en que se consumen
        los alimentos.
      </p>
      <p data-id="element-53" data-mp="1eojmjvdt07hm">Ahí es donde entra la secuenciación de alimentos.</p>

      <h2 className={headingClass} data-id="element-54" data-mp="2xdmeidwpe0ln">Qué es la secuenciación de alimentos</h2>
      <SectionFigure {...sectionImages.sequencing} data-id="element-55" data-mp="32bxqau6k6l6i" />
      <p data-id="element-56" data-mp="32ch727kh33iy">
        La secuenciación de alimentos es una estrategia práctica que consiste en comer los grupos
        de alimentos en un orden específico para favorecer una respuesta glucémica más estable.
      </p>
      <p data-id="element-57" data-mp="3bdx7bmhkpp9g">En términos simples, muchas personas aplican una estructura como esta:</p>
      <ol className="space-y-3 rounded-sm border border-neutral-200 bg-paper p-5" data-id="element-58" data-mp="3gb74job7f57u">
        {[{
        step: '1',
        title: 'Primero fibra',
        detail: 'vegetales, ensalada, hojas verdes o verduras.'
      }, {
        step: '2',
        title: 'Luego proteína y grasas saludables',
        detail: 'huevo, pollo, pescado, carne magra, tofu, aguacate, aceite de oliva, frutos secos.'
      }, {
        step: '3',
        title: 'Al final carbohidratos',
        detail: 'arroz, pasta, pan, papa, tortilla, fruta o postre.'
      }].map(item => <li key={item.step} className="flex gap-3.5" data-id="element-59" data-mp="1z0gsvu7clskl">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand font-sans text-sm font-bold text-white" data-id="element-60" data-mp="1yxvszaqm1mph">
              {item.step}
            </span>
            <span className="text-[1.02rem] leading-relaxed" data-id="element-61" data-mp="28wtowyqd8iii">
              <strong className="font-semibold" data-id="element-62" data-mp="2mornifmgaw0f">{item.title}:</strong> {item.detail}
            </span>
          </li>)}
      </ol>
      <p data-id="element-63" data-mp="2inod48n69s6l">
        La idea es que la fibra, la proteína y las grasas saludables puedan ayudar a ralentizar la
        absorción de los carbohidratos que vienen después.
      </p>
      <p data-id="element-64" data-mp="2rpxikk3l2vd4">
        No se trata de prohibir alimentos. No se trata de vivir a dieta. No se trata de comer
        perfecto.
      </p>
      <p className="font-semibold" data-id="element-65" data-mp="12bsi9ixyp7l7">Se trata de darle al cuerpo una mejor secuencia.</p>

      <h2 className={headingClass} data-id="element-66" data-mp="xcfsapbsnkee">El «escudo de fibra»: una explicación sencilla</h2>
      <SectionFigure {...sectionImages.fiberShield} data-id="element-67" data-mp="38ygktvercgrc" />
      <p data-id="element-68" data-mp="3dwrti2tbgl7z">Imagina que tu comida entra a tu sistema como una fila.</p>
      <p data-id="element-69" data-mp="3dz4ekoudfbp9">
        Si lo primero que llega es pan, arroz, pasta, jugo o algo dulce, tu cuerpo puede recibir
        una carga rápida de glucosa.
      </p>
      <p data-id="element-70" data-mp="3466onxn1ukgm">
        Pero si antes de eso entra fibra, proteína y grasa saludable, la digestión puede volverse
        más gradual. Muchas personas sienten más saciedad, menos sueño después de comer y menos
        urgencia por buscar algo dulce unas horas después.
      </p>
      <p data-id="element-71" data-mp="2g9j4hxu3wu18">A esta estrategia algunas personas la llaman de forma simple «escudo de fibra».</p>
      <p data-id="element-72" data-mp="2l845hzh6nhqq">No porque sea magia. No porque cure nada. No porque funcione igual en todo el mundo.</p>
      <p data-id="element-73" data-mp="ehwikap2npbm">
        Sino porque ayuda a muchas mujeres a entender que no siempre necesitan eliminar sus
        comidas favoritas. A veces, necesitan aprender a ordenarlas mejor.
      </p>

      <h2 className={headingClass} data-id="element-74" data-mp="jen072giysz5">Un ejemplo práctico</h2>
      <SectionFigure {...sectionImages.example} data-id="element-75" data-mp="33bqjkw8j5jph" />
      <p data-id="element-76" data-mp="2ydyt86nja5ne">Veamos una comida común:</p>
      <p className="font-semibold" data-id="element-77" data-mp="2yf02q6owik30">Arroz, pollo, ensalada y una bebida.</p>
      <p data-id="element-78" data-mp="2tmp1n0ifcs8v">
        Muchas personas empezarían por el arroz o acompañarían cada bocado con carbohidrato desde
        el inicio.
      </p>
      <p data-id="element-79" data-mp="2onm8adbt4agq">Con secuenciación, la estructura sería diferente:</p>
      <ListBlock items={['Primero: ensalada o vegetales.', 'Después: pollo o proteína principal.', 'Luego: arroz o carbohidrato.', 'Finalmente: si hay postre, dejarlo para el final y no comerlo solo con el estómago vacío.']} data-id="element-80" data-mp="2jpkdww8xkopy" />
      <p data-id="element-81" data-mp="25pjdtj0hplbj">Es el mismo plato, pero con una estrategia distinta.</p>
      <p data-id="element-82" data-mp="25re0b1retlhq">
        Para muchas mujeres, este cambio es más fácil de sostener que una dieta restrictiva,
        porque no se basa en miedo a la comida, sino en orden, saciedad y consciencia.
      </p>

      <h2 className={headingClass} data-id="element-83" data-mp="3vtswmcbrd52">
        Por qué esto puede ser especialmente relevante después de los 30
      </h2>
      <SectionFigure {...sectionImages.after30} data-id="element-84" data-mp="ggio49uu1mdk" />
      <p data-id="element-85" data-mp="gf7kca1e0emg">Después de los 30, muchas mujeres notan cambios que antes no tenían:</p>
      <ListBlock items={['Más cansancio después de comer.', 'Más dificultad para bajar grasa abdominal.', 'Más ansiedad por dulce.', 'Menos tolerancia a ciertos alimentos.', 'Más inflamación o pesadez.', 'Más impacto del estrés y del mal sueño.']} data-id="element-86" data-mp="l6z6879llw69" />
      <p data-id="element-87" data-mp="3tnannw1jea1k">
        Esto no significa que el cuerpo esté «dañado». Pero sí puede indicar que necesita una
        estrategia más inteligente que simplemente reducir calorías.
      </p>
      <p data-id="element-88" data-mp="7glyo60mcjhg">
        El estrés, el descanso, la actividad física, el ciclo hormonal y la composición de cada
        comida pueden influir en cómo una mujer se siente durante el día.
      </p>
      <p data-id="element-89" data-mp="1ity06a8woq0g">
        Por eso, un enfoque práctico como la secuenciación de alimentos puede ser un primer paso
        útil para recuperar sensación de control.
      </p>

      <h2 className={headingClass} data-id="element-90" data-mp="3pkmm82kp35h2">Lo que este enfoque NO es</h2>
      <SectionFigure {...sectionImages.notThis} data-id="element-91" data-mp="3pis1ao9fbdcz" />
      <p data-id="element-92" data-mp="3ubbh7rvl2kky">Es importante aclararlo:</p>
      <ListBlock items={['No es una cura.', 'No reemplaza un tratamiento médico.', 'No promete pérdida de peso garantizada.', 'No es una dieta extrema.', 'No exige eliminar todos los carbohidratos.', 'No está diseñado para diagnosticar diabetes ni ninguna condición médica.']} data-id="element-93" data-mp="3ucd36re50rhg" />
      <p data-id="element-94" data-mp="3bktzeicah9ed">
        Es una herramienta educativa de alimentación consciente que puede ayudar a muchas
        personas a organizar mejor sus comidas y observar cómo responde su cuerpo.
      </p>
      <p data-id="element-95" data-mp="qvnw94an7fr9">
        Si una persona tiene diabetes, hipoglucemia, resistencia a la insulina diagnosticada,
        embarazo, lactancia, trastornos de la conducta alimentaria o toma medicamentos que afectan
        la glucosa, debe consultar con un profesional de salud antes de hacer cambios en su
        alimentación.
      </p>

      <h2 className={headingClass} data-id="element-96" data-mp="2bdf7m907bxsn">
        El método 1-2-3 que muchas mujeres están empezando a probar
      </h2>
      <SectionFigure {...sectionImages.method} data-id="element-97" data-mp="26kdrjxo9ds7h" />
      <p data-id="element-98" data-mp="21nnb398jkulj">
        A partir de este principio, se creó el{' '}
        <strong className="font-semibold" data-id="element-99" data-mp="21l9gf1hrxkei">
          Método Glucosa Estable: El Código Anti-Antojos
        </strong>
        , una guía práctica de 21 días diseñada para mujeres que quieren aprender a organizar sus
        comidas de forma más estratégica.
      </p>
      <p data-id="element-100" data-mp="1no45an37dvce">El método enseña cómo aplicar la secuenciación en situaciones reales, como:</p>
      <ListBlock items={['Desayunos rápidos.', 'Almuerzos fuera de casa.', 'Cenas familiares.', 'Antojos de media tarde.', 'Comidas con pan, arroz, tortilla o pasta.', 'Eventos sociales.', 'Días de estrés.', 'Momentos del ciclo en los que aumentan los antojos.']} data-id="element-101" data-mp="1ipaz7fq8x6m9" />
      <p data-id="element-102" data-mp="1g3jsiv55yqwr">La propuesta no es comer perfecto. La propuesta es tener un mapa claro.</p>
      <p data-id="element-103" data-mp="11excssbmjx50">
        Porque muchas mujeres no necesitan otra dieta imposible. Necesitan saber qué hacer cuando
        llega el antojo, cuando están cansadas, cuando comen fuera, cuando no tienen tiempo o
        cuando sienten que su cuerpo les pide azúcar.
      </p>

      <h2 className={headingClass} data-id="element-104" data-mp="1u4dzd9ckcjtb">¿Es este enfoque para ti?</h2>
      <p data-id="element-105" data-mp="1z2xkj7jc1rkm">
        Este tipo de estrategia puede interesarte si te identificas con algunas de estas
        situaciones:
      </p>
      <ListBlock items={['Sientes sueño o pesadez después de comer.', 'Te dan antojos intensos de dulce en la tarde.', 'Empiezas el día con café y algo rápido, pero luego tienes hambre intensa.', 'Te cuesta sentir saciedad aunque «comas bien».', 'Sientes que tu energía sube y baja durante el día.', 'Te frustran las dietas restrictivas.', 'Quieres aprender a comer con más estructura sin eliminar todo lo que te gusta.']} data-id="element-106" data-mp="1l2d7ks03mpz1" />
      <p data-id="element-107" data-mp="193avvawpgat">
        No se trata de hacerlo perfecto. Se trata de entender tu cuerpo y empezar con cambios
        simples.
      </p>
    </div>;
}